import { Injectable, inject } from '@angular/core';
import { selectorCoreInstanceInfo } from '@bepatient/core';
import { ThemeService } from './theme.service';
import { Capacitor } from '@capacitor/core';
import {
  Camera,
  CameraSource,
  ImageOptions,
  CameraResultType,
  Photo
} from '@capacitor/camera';
import { FilePicker, PickFilesResult } from '@capawesome/capacitor-file-picker';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Browser } from '@capacitor/browser';
import { Haptics } from '@capacitor/haptics';
import { Platform } from '@ionic/angular';
import { Keyboard } from '@capacitor/keyboard';
import { Action, MemoizedSelector, select, Store } from '@ngrx/store';
import {
  NativeSettings,
  AndroidSettings,
  IOSSettings
} from 'capacitor-native-settings';
import {
  BehaviorSubject,
  combineLatest,
  from,
  MonoTypeOperatorFunction,
  Observable
} from 'rxjs';
import {
  delay,
  distinctUntilChanged,
  filter,
  map,
  retryWhen,
  scan,
  take,
  tap
} from 'rxjs/operators';
import {
  SharedPopupService,
  getAccessibleTextColor,
  getThemeAwareColor
} from '@bepatient/shared';
import { TranslateService } from '@ngx-translate/core';
import { RRule } from 'rrule';
import { addMonths, format } from 'date-fns';
import { Network } from '@capacitor/network';
import { Device } from '@capacitor/device';

export function retryWithDelay<T>(
  time: number,
  count = 1
): MonoTypeOperatorFunction<T> {
  return input =>
    input.pipe(
      retryWhen(errors =>
        errors.pipe(
          scan((acc, error) => ({ count: acc.count + 1, error }), {
            count: 0,
            error: undefined as any
          }),
          tap(current => {
            if (current.count >= count) {
              throw current.error;
            }
          }),
          delay(time)
        )
      )
    );
}

export interface RRuleResponse {
  code: RRuleResponseCode;
  type: string;
  message: string;
}

export enum RRuleResponseCode {
  SUCCESS = 0,
  END_MISSING = 1,
  INVALID_FREQUENCY = 2,
  INVALID_RRULE = 3
}

@Injectable()
export class CommonUtilsService {
  private store = inject<Store<any>>(Store);
  private platform = inject(Platform);
  private sharedPopupService = inject(SharedPopupService);
  private translate = inject(TranslateService);
  private themeService = inject(ThemeService);

  private keyboardOpenedSource = new BehaviorSubject<boolean>(false);
  keyboardOpened$ = this.keyboardOpenedSource.asObservable();
  keyboardClosed$ = this.keyboardOpened$.pipe(map(v => !v));

  get instanceColor$(): Observable<string> {
    return this.store.pipe(
      select(selectorCoreInstanceInfo),
      filter(instance => !!instance),
      map(instance => instance.appColor)
    );
  }

  /**
   * Reactive, theme-aware study color. Re-emits the freshly adjusted color
   * whenever the configured instance color OR the active theme changes, so
   * surfaces bound via `| async` update immediately on a light ↔ dark / high
   * contrast toggle without a page reload.
   *
   * Reads the *raw* `instanceColor` (not the pre-adjusted `appColor`) and runs
   * it through the canonical `getThemeAwareColor`, so there is no risk of
   * double-adjustment. See docs/adr/0001-contrast-utility-consolidation.md.
   */
  get instanceThemeColor$(): Observable<string> {
    return combineLatest([
      this.store.pipe(
        select(selectorCoreInstanceInfo),
        filter(instance => !!instance),
        map(instance => instance.instanceColor || instance.appColor)
      ),
      this.themeService.themeChange$
    ]).pipe(
      map(([color]) => getThemeAwareColor(color)),
      distinctUntilChanged()
    );
  }

  constructor() {
    if (Capacitor.isNativePlatform()) {
      Keyboard.addListener('keyboardDidHide', () =>
        this.keyboardOpenedSource.next(false)
      );
      Keyboard.addListener('keyboardWillShow', () =>
        this.keyboardOpenedSource.next(true)
      );
    }
  }

  storeDispatch(action: Action): void {
    this.store.dispatch(action);
  }

  storeSelect<T>(selector: MemoizedSelector<object, T>): Observable<T> {
    return this.store.pipe(select(selector));
  }

  storeSelectOne<T>(
    selector: MemoizedSelector<object, T>,
    waitWhileUndefined = false
  ): Promise<T> {
    return new Promise<T>(resolve =>
      this.store
        .pipe(
          select(selector),
          filter(result => !waitWhileUndefined || !!result),
          take(1)
        )
        .subscribe(result => resolve(result))
    );
  }

  textContrastColor(backgroundColor: string): string {
    // Delegates to the canonical WCAG-AA utility so every surface computes the
    // same contrast text color for a given background. See
    // docs/adr/0001-contrast-utility-consolidation.md.
    return getAccessibleTextColor(backgroundColor);
  }

  vibrate(time: number): void {
    Haptics.vibrate({ duration: time });
  }

  async checkCameraPermission(checkAccessType?: string) {
    const { camera: cameraPermission, photos: photoPermission } =
      await Camera.checkPermissions();
    const popupTitle = this.translate.instant('PERMISSION_POPUP.TITLE');
    const popupMsg = this.translate.instant('PERMISSION_POPUP.CAMERA_MESSAGE');
    if (cameraPermission === 'denied' && photoPermission === 'denied') {
      if (Capacitor.isNativePlatform()) {
        this.openNativeOSSettingPopup(popupTitle, popupMsg);
      }
    } else if (
      checkAccessType &&
      ((checkAccessType === CameraSource.Photos &&
        photoPermission === 'denied') ||
        (checkAccessType === CameraSource.Camera &&
          cameraPermission === 'denied'))
    ) {
      if (Capacitor.isNativePlatform()) {
        this.openNativeOSSettingPopup(popupTitle, popupMsg);
      }
    }
  }

  takePicture(
    options: ImageOptions = { resultType: CameraResultType.DataUrl }
  ): Observable<Photo> {
    this.checkCameraPermission(options.source);
    return from(
      Camera.getPhoto({
        quality: 75,
        width: 1080,
        height: 1080,
        allowEditing: false,
        resultType: CameraResultType.DataUrl,
        ...options
      })
    );
  }

  /**
   * Popup to open Native OS setting for application details
   * @param popupTitle value - Title for popup
   * @param popupMessage value - Information message for popup
   * @example
   * openNativeOSSettingPopup('Error', 'Camera permission is denied, please grant to capture photo')
   */
  openNativeOSSettingPopup(
    popupTitle: string,
    popupMessage: string,
    nativeSettingiOS: IOSSettings = IOSSettings.App,
    nativeSettingAndroid: AndroidSettings = AndroidSettings.ApplicationDetails
  ) {
    const instanceInfo = this.store.pipe(
      select(selectorCoreInstanceInfo),
      take(1)
    );
    const translations = [
      this.translate.instant('PERMISSION_POPUP.OK'),
      this.translate.instant('PERMISSION_POPUP.ALLOW')
    ];
    return new Promise<number>(resolve => {
      this.sharedPopupService
        .simpleAlert({
          color: instanceInfo && instanceInfo['appColor'],
          title: popupTitle,
          message: popupMessage,
          buttons: [translations[0], translations[1]]
        })
        .then(index => {
          if (index === 1) {
            NativeSettings.open({
              optionAndroid: nativeSettingAndroid,
              optionIOS: nativeSettingiOS
            });
          }
          resolve(index);
        });
    });
  }

  chooseFile(): Observable<PickFilesResult> {
    return from(
      FilePicker.pickFiles({
        types: ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'],
        readData: true
      })
    );
  }

  // TODO-CAP-MIGRATION: When needed, convert to Capacitor equivalent, https://capacitorjs.com/docs/basics/utilities#capacitorconvertfilesrc
  // async convertFile(uri) {
  //   try {
  //     if (this.platform.is('android')) {
  //       const path = await this.filePath.resolveNativePath(uri);
  //       return await this.base64.encodeFile(path);
  //     } else {
  //       const path: string = uri.substring(0, uri.lastIndexOf('/') + 1);
  //       const fileName: string = uri.split('/').pop();
  //       // return await this.file.readAsDataURL(path, fileName);
  //       return await Filesystem.readFile({
  //         path: path + fileName,
  //       });
  //     }
  //   } catch (err) {}
  // }

  openExternalUrlInBrowser(url: string): void {
    if (Capacitor.isNativePlatform()) {
      Browser.open({ url });
    } else {
      window.open(url, '_blank');
    }
  }

  public generateFilename(blobType: string): string {
    let extension = null;
    let prefix = null;
    switch (blobType) {
      case 'image/jpeg':
        prefix = 'img';
        extension = '.jpg';
        break;

      case 'image/png':
        prefix = 'img';
        extension = '.png';
        break;

      case 'image/svg+xml':
        prefix = 'svg';
        extension = '.svg';
        break;

      case 'application/pdf':
        prefix = 'doc';
        extension = '.pdf';
        break;

      case 'application/vnd.ms-excel':
        prefix = 'excel';
        extension = '.xls';
        break;

      case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
        prefix = 'excel-OpenXML';
        extension = '.xlsx';
        break;

      case 'application/msword':
        prefix = 'word';
        extension = '.doc';
        break;

      case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        prefix = 'word-OpenXML';
        extension = '.docx';
        break;
    }
    const now = new Date();
    const date = `${now.getDate()}-${now.getMonth()}-${now.getFullYear()}-${now.getHours()}-${now.getMinutes()}-${now.getSeconds()}`;

    return `${prefix}-${date}${extension}`;
  }

  hexToRGBA(hex: string, alpha = 1): string {
    // TODO :: use a regex for #rgb / #rrggbb / #rrggbbaa
    const parsedHex = parseInt(hex.substr(1), 16);
    /* tslint:disable:no-bitwise */
    const r = parsedHex >> 16;
    const g = (parsedHex >> 8) & 0xff;
    const b = parsedHex & 0xff;
    /* tslint:enable:no-bitwise */
    return hex.length === 7
      ? `rgba(${r}, ${g}, ${b}, ${alpha})`
      : 'rgba(255, 0, 0, 1)';
  }
  /**
   * Check if directory is present
   * @param directory {string} - directory name to check
   * @returns
   */
  checkDirectoryExists(directory: string) {
    const dirPath = this.platform.is('android') ? directory : `/`;
    return Filesystem.readdir({
      path: dirPath,
      directory: Directory.Documents
    });
  }

  /**
   * Create a directory
   * @param directory {string} - directory name to create
   * @returns
   */
  createDirectory(directory: string) {
    const dirPath = this.platform.is('android') ? directory : `/`;
    return Filesystem.mkdir({
      path: dirPath,
      directory: Directory.Documents
    });
  }

  /**
   * Check fileSystem permission;
   * On android need fileSystem permission from user
   * @returns
   */
  async checkFileSystemPermission() {
    const storagePermission = await (
      await Filesystem.checkPermissions()
    ).publicStorage;
    return storagePermission;
  }

  /**
   * Validate RRule string and return validity status
   * @param rruleString
   * @returns {code: number, type: string, message: string}
   */
  validateRRule(rruleString): RRuleResponse {
    try {
      // Check rrule string contains FREQ=ANYTIME, as ANYTIME is not the standard rrule frequency type.
      const regex = /FREQ=ANYTIME/i;
      const containsAnytime = regex.test(rruleString);
      if (containsAnytime) {
        throw {
          code: RRuleResponseCode.INVALID_FREQUENCY,
          type: 'error',
          message: 'FREQ=ANYTIME is invalid in RRule'
        };
      }
      // Use RRule parser to get options form the provided rrule string
      const rrule = RRule.fromString(rruleString);

      // Check if both UNTIL and COUNT parameters are missing
      if (!rrule.options.until && !rrule.options.count) {
        throw {
          code: RRuleResponseCode.END_MISSING,
          type: 'error',
          message: 'Both UNTIL and COUNT parameters are missing.'
        };
      }
      return {
        code: RRuleResponseCode.SUCCESS,
        type: 'success',
        message: 'Valid RRule'
      };
    } catch (error) {
      if (error && typeof error === 'object' && error.code) {
        return error;
      }
      return {
        code: RRuleResponseCode.INVALID_RRULE,
        type: 'error',
        message: 'Invalid RRule'
      };
    }
  }

  /**
   * Format the rrule string received from the server,
   * to suitable format before parsing the occurrence.
   * @param rruleString
   * @param errorCode
   * @returns string
   */
  formatRRule(rruleString, errorCode: RRuleResponseCode): string {
    switch (errorCode) {
      case RRuleResponseCode.SUCCESS:
        return rruleString;

      case RRuleResponseCode.END_MISSING:
        // If end date is missing, add end date 2 months from now.
        if (!rruleString.includes('UNTIL')) {
          let rruleEndDate = format(addMonths(new Date(), 2), 'yyyyMMdd');
          rruleString += ';UNTIL=' + rruleEndDate;
        }
        return rruleString;

      case RRuleResponseCode.INVALID_FREQUENCY:
        // For Frequency ANYTIME, update the start date to Today date and update Frequency and count to show in today activity.
        let validatedRRule = rruleString.replace(
          /DTSTART:(.*?)(?=\r)/,
          `DTSTART:${format(new Date(), 'yyyyMMdd')}T000000`
        );
        validatedRRule = validatedRRule.replace(
          /FREQ=ANYTIME/i,
          'FREQ=DAILY;COUNT=1;INTERVAL=1'
        );
        return validatedRRule;

      default:
        return rruleString;
    }
  }

  /**
   * Check network connection and speed and return a result with level, ok, message and optional speed/duration.
   *
   * Check flow:
   * 1. Offline check – use Capacitor Network.getStatus(); if not connected, return immediately with level 'none'.
   * 2. Resolve test URL – baseUrl + '/rest/network-check-image' when provided, else fallback image URL.
   * 3. Warmup – single HEAD request with timeout to establish connection/DNS; when baseUrl is set, non-2xx (e.g. 404) fails the check.
   * 4. Speed tests – run NUMBER_OF_TESTS GET requests with cache-busting, each with timeout and up to one retry; when baseUrl is set, non-2xx is treated as failed attempt.
   * 5. Result – take median of successful run durations, derive speed (Kbps) from known file size, then map to level/message (low below 200 Kbps, sufficient below 1000 Kbps, very_good otherwise).
   * 6. On any unrecovered error (timeout, all tests failed, warmup 404), return connection error message.
   *
   * @param baseUrl Optional base URL from instancesData (current environment). If provided, file URL is baseUrl + '/rest/network-check-image' and response status (e.g. 404) is checked.
   * @returns Promise with level ('none' | 'low' | 'sufficient' | 'very_good'), ok, message, and optionally speedKbps and duration.
   */
  async checkNetworkConnection(baseUrl?: string): Promise<{
    level: string;
    ok: boolean;
    message: string;
    speedKbps?: number;
    duration?: number;
  }> {
    // --- 1. Offline check ---
    const status = await Network.getStatus();
    if (!status.connected) {
      return {
        level: 'none',
        ok: false,
        message: this.translate.instant('NO_NETWORK_POPUP.MESSAGE')
      };
    }

    // --- 2. Resolve test URL and file size ---
    const isValidBaseUrl =
      typeof baseUrl === 'string' &&
      baseUrl.trim() !== '' &&
      (() => {
        try {
          const u = new URL(baseUrl);
          return u.protocol === 'http:' || u.protocol === 'https:';
        } catch {
          return false;
        }
      })();
    const effectiveBaseUrl = isValidBaseUrl
      ? baseUrl!.trim().replace(/\/$/, '')
      : null;
    const fileUrl = effectiveBaseUrl
      ? `${effectiveBaseUrl}/rest/network-check-image`
      : 'https://wiki-smart.banookgroup.com/wp-content/uploads/2024/09/b_research_square_black-768x768.png';
    const fileSizekB = effectiveBaseUrl ? 180.49 : 20;

    const GLOBAL_TIMEOUT_MS = 20000; // Maximum time for entire check
    const REQUEST_TIMEOUT_MS = 8000; // Reduced per-request timeout
    const NUMBER_OF_TESTS = 3;
    const DELAY_AFTER_WARMUP_MS = 80;
    const DELAY_BETWEEN_TESTS_MS = 150;
    const MAX_RETRIES_PER_TEST = 1; // Reduced from 2 to 1

    // When effectiveBaseUrl is set we hit our own API: use cors so we can detect 404. Fallback URL stays no-cors.
    const canCheckStatus = Boolean(effectiveBaseUrl);
    const globalStartTime = performance.now();

    // Check if global timeout exceeded
    const isGlobalTimeoutExceeded = () => {
      return performance.now() - globalStartTime > GLOBAL_TIMEOUT_MS;
    };

    // Abort request after timeout to avoid hanging on slow/stuck networks.
    const fetchWithTimeout = (url: string, options: RequestInit) => {
      const controller = new AbortController();
      const timeoutId = setTimeout(
        () => controller.abort(),
        REQUEST_TIMEOUT_MS
      );
      return fetch(url, { ...options, signal: controller.signal }).finally(() =>
        clearTimeout(timeoutId)
      );
    };

    const fetchOptions: RequestInit = {
      cache: 'no-store',
      mode: canCheckStatus ? 'cors' : 'no-cors',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache'
      }
    };

    try {
      // --- 3. Warmup (HEAD or GET fallback) ---
      if (isGlobalTimeoutExceeded()) {
        throw new Error('Global timeout exceeded before warmup');
      }

      const warmupRes = await fetchWithTimeout(fileUrl, {
        ...fetchOptions,
        method: 'HEAD'
      }).catch(() => null);

      // Handle 405/501 (Method Not Allowed/Not Implemented) by trying GET
      if (warmupRes && (warmupRes.status === 405 || warmupRes.status === 501)) {
        const getWarmupRes = await fetchWithTimeout(fileUrl, {
          ...fetchOptions,
          method: 'GET'
        }).catch(() => null);

        if (canCheckStatus && getWarmupRes && !getWarmupRes.ok) {
          throw new Error(
            `Network check endpoint returned ${getWarmupRes.status}`
          );
        }
      } else if (canCheckStatus && warmupRes && !warmupRes.ok) {
        throw new Error(`Network check endpoint returned ${warmupRes.status}`);
      }

      await new Promise(resolve => setTimeout(resolve, DELAY_AFTER_WARMUP_MS));

      // --- 4. Speed tests (GET, median of successful runs) ---
      const testResults: number[] = [];

      for (let i = 0; i < NUMBER_OF_TESTS; i++) {
        if (isGlobalTimeoutExceeded()) {
          console.warn('Global timeout exceeded, stopping tests');
          break;
        }

        const testUrl = `${fileUrl}?t=${Date.now()}&test=${i}`;
        let lastError: unknown;

        for (let attempt = 0; attempt <= MAX_RETRIES_PER_TEST; attempt++) {
          if (isGlobalTimeoutExceeded()) {
            break;
          }

          const start = performance.now();
          try {
            const response = await fetchWithTimeout(testUrl, fetchOptions);
            if (canCheckStatus && !response.ok) {
              throw new Error(`Test ${i + 1} got ${response.status}`);
            }
            const duration = performance.now() - start;
            const speedKbps = (fileSizekB * 8) / (duration / 1000);
            testResults.push(speedKbps);
            break;
          } catch (error) {
            lastError = error;
            if (attempt === MAX_RETRIES_PER_TEST) {
              console.warn(`Test ${i + 1} failed after retry:`, lastError);
            }
          }
        }

        if (i < NUMBER_OF_TESTS - 1 && !isGlobalTimeoutExceeded()) {
          await new Promise(resolve =>
            setTimeout(resolve, DELAY_BETWEEN_TESTS_MS)
          );
        }
      }

      if (testResults.length === 0) {
        if (isGlobalTimeoutExceeded()) {
          throw new Error('Network check timed out');
        }
        throw new Error('All speed tests failed');
      }

      // --- 5. Derive median speed and map to level/message ---
      testResults.sort((a, b) => a - b);
      const medianIndex = Math.floor(testResults.length / 2);
      const speedKbps =
        testResults.length % 2 === 0
          ? (testResults[medianIndex - 1] + testResults[medianIndex]) / 2
          : testResults[medianIndex];

      let level = '';
      let message = '';
      let ok = true;

      if (speedKbps < 200) {
        level = 'low';
        ok = false;
        message = this.translate.instant('CHECK_CONNECTION.CONNECTION_LOW');
      } else if (speedKbps < 1000) {
        level = 'sufficient';
        ok = true;
        message = this.translate.instant(
          'CHECK_CONNECTION.CONNECTION_SUFFICENT'
        );
      } else {
        level = 'very_good';
        ok = true;
        message = this.translate.instant('CHECK_CONNECTION.CONNECTION_GOOD');
      }

      return {
        level,
        ok,
        message,
        speedKbps: Math.round(speedKbps),
        duration: Math.round(((fileSizekB * 8) / speedKbps) * 1000)
      };
    } catch (error) {
      // --- 6. Any unrecovered error (timeout, all tests failed, warmup 404) ---
      const isTimeout =
        error instanceof Error && error.message.includes('timeout');
      return {
        level: 'low',
        ok: false,
        message: isTimeout
          ? this.translate.instant('CHECK_CONNECTION.CONNECTION_TIMEOUT')
          : this.translate.instant('CHECK_CONNECTION.CONNECTION_ERROR')
      };
    }
  }
}
